import React from 'react';
import * as yup from 'yup';
import {QuickSettingsForm, FormFieldConfig} from '@/components/forms';
import i18n from 'i18n';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {RestrictedFieldInput} from '@/components/utils/DataNotAvailable';

interface TeamFormValues {
    name: string;
    shortname: string;
}

interface TeamSettingsFormProps {
    teamId?: number;
    onSave: () => void;
}

const TeamSettingsForm: React.FC<TeamSettingsFormProps> = React.memo((
    {
        teamId = 0,
        onSave = () => {},
    },
) => {
    const {userData} = useOptimizedConfig();
    const restrictedFieldKeys: string[] = userData?.restrictedFieldKeys || [];
    const teamRestricted = restrictedFieldKeys.includes('team_pii');

    if (teamRestricted) {
        return (
            <div>
                <div className="mb-3">
                    <label className="form-label">{i18n.t('jt_team_name.text')}</label>
                    <RestrictedFieldInput />
                </div>
                <div className="mb-3">
                    <label className="form-label">{i18n.t('jt_team_unique.text')}</label>
                    <RestrictedFieldInput />
                </div>
            </div>
        );
    }

    const initialValues: TeamFormValues = {
        name: '',
        shortname: '',
    };

    const customErrorHandling = (error: any, formik: any) => {
        if (error.response?.status === 400) {
            formik.setFieldError('shortname', error.response?.data?.message ?? i18n.t('invalid_data.text'));
            return true;
        }
        return false;
    };

    const fields: FormFieldConfig[] = [
        {
            name: 'name',
            label: i18n.t('jt_team_name.text'),
            type: 'text',
            required: true,
            validation: yup.string()
                .max(100, i18n.t('jt_ts_max_name_length.text'))
                .required(i18n.t('jt_ts_required_name.text')),
        },
        {
            name: 'shortname',
            label: i18n.t('jt_team_unique.text'),
            type: 'text',
            required: true,
            disabled: !!teamId,
            validation: yup.string()
                .max(100, i18n.t('jt_ts_max_shortname_length.text'))
                .required(i18n.t('jt_ts_required_shortname.text')),
        },
    ];

    return (
        <QuickSettingsForm
            entityId={teamId || undefined}
            crudConfig={{
                resourceUrl: window.URLS.teamsResource,
                storeActionPath: 'teams.addOrEdit',
                successMessageKey: 'jt_st_team_success_action.text',
                entityName: 'team'
            }}
            fields={fields}
            initialValues={initialValues}
            onSave={onSave}
            customErrorHandling={customErrorHandling}
            additionalStoreActions={[
                {
                    path: 'activeStoryObjectDetails.setReload',
                    value: true,
                },
                {
                    path: 'teams.setReload',
                    value: true,
                },
            ]}
        />
    );
});


export default TeamSettingsForm; 